import React, { useEffect, useState } from "react";
import { PieChart, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { profit } from "@/lib/api";
import { formatIDR } from "@/lib/format";

export default function Profit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { profit.summary().then(setData).catch(() => {}).finally(() => setLoading(false)); }, []);

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-emerald-600" /></div>;

  const products = data?.products || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-gray-900">Profit Dashboard</h1>
        <p className="text-sm text-gray-500 mt-1">Kalkulasi profit bersih per SKU. Tahu produk mana yang benar-benar untung.</p>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Card className="border-gray-200"><CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Total Revenue</div>
          <div className="mt-2 font-heading text-3xl font-bold text-gray-900">{formatIDR(data?.total_revenue || 0)}</div>
        </CardContent></Card>
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white"><CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">Net Profit</div>
          <div className="mt-2 font-heading text-3xl font-bold text-emerald-700">{formatIDR(data?.total_net_profit || 0)}</div>
        </CardContent></Card>
        <Card className="border-gray-200"><CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-wider text-gray-500">Net Margin</div>
          <div className="mt-2 font-heading text-3xl font-bold text-gray-900">{data?.overall_margin || 0}%</div>
        </CardContent></Card>
      </div>

      <Card className="border-gray-200 overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-heading font-semibold text-gray-900">Profit per SKU</h3>
          <p className="text-xs text-gray-500 mt-0.5">Termasuk HPP, fee marketplace, iklan, dan packaging.</p>
        </div>
        {products.length === 0 ? (
          <div className="py-16 text-center">
            <PieChart className="mx-auto text-gray-300" size={56} />
            <h3 className="mt-4 font-heading text-lg font-semibold text-gray-900">Belum ada data profit</h3>
            <p className="mt-1 text-sm text-gray-500">Tambahkan produk & order untuk melihat kalkulasi profit.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider border-b border-gray-200">
                <tr>
                  <th className="px-4 py-3">Produk</th>
                  <th className="px-4 py-3">Sold</th>
                  <th className="px-4 py-3">Revenue</th>
                  <th className="px-4 py-3">HPP+Fee+Iklan</th>
                  <th className="px-4 py-3">Net Profit</th>
                  <th className="px-4 py-3">Margin</th>
                  <th className="px-4 py-3">Min. Sell Price</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {products.map((p) => {
                  const isProfitable = p.net_profit > 0;
                  const cost = (p.hpp * p.units_sold) + p.marketplace_fee + p.total_ads + p.total_packaging;
                  return (
                    <tr key={p.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="font-semibold text-gray-900">{p.product_name}</div>
                        <div className="text-xs text-gray-500 font-mono">{p.sku}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{p.units_sold}</td>
                      <td className="px-4 py-3 text-gray-900 font-semibold">{formatIDR(p.revenue)}</td>
                      <td className="px-4 py-3 text-red-600">-{formatIDR(cost)}</td>
                      <td className={`px-4 py-3 font-bold ${isProfitable ? "text-emerald-600" : "text-red-500"}`}>{formatIDR(p.net_profit)}</td>
                      <td className="px-4 py-3">
                        <Badge className={`border ${p.net_margin >= 30 ? "bg-emerald-50 text-emerald-700 border-emerald-200" : p.net_margin >= 10 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-red-50 text-red-700 border-red-200"}`}>
                          {p.net_margin >= 0 ? <TrendingUp size={12} className="mr-1" /> : <TrendingDown size={12} className="mr-1" />}
                          {p.net_margin}%
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-gray-700">{formatIDR(p.min_selling_price)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card className="border-amber-200 bg-amber-50/30">
        <CardContent className="p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-amber-700">Tip</div>
          <p className="mt-1 text-sm text-gray-700">
            Produk dengan <b>net margin di bawah 10%</b> berpotensi <b>rugi tersembunyi</b> setelah biaya iklan & retur. Pertimbangkan naikkan harga, kurangi biaya iklan, atau bundling dengan produk margin tinggi.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
